import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoldSplash } from './GoldSplash.js';
import { GildButton } from './GildButton.js';

/**
 * Splash — the entry cover for signed-out visitors arriving at the domain.
 *
 * Shares the live-WebGL gilded "Gilding" wordmark with the pre-launch
 * ComingSoon page, but instead of holding the visitor out it invites them IN:
 * once the leafing settles, an "Enter site" CTA rises, and tapping it reveals
 * the wallet-connect Landing. Full-bleed and fixed so it covers the app chrome
 * underneath while it's shown. Its own dark-luxury world, independent of the
 * app theme.
 */
export function Splash({ onEnter }: { onEnter: () => void }) {
  const [revealed, setRevealed] = useState(false);
  const regildRef = useRef<(() => void) | null>(null);
  const onSettled = useCallback(() => setRevealed(true), []);
  const bindRegild = useCallback((fn: () => void) => {
    regildRef.current = fn;
  }, []);
  // Safety net: never strand the CTA if the settle callback is somehow missed.
  useEffect(() => {
    const t = window.setTimeout(() => setRevealed(true), 6000);
    return () => window.clearTimeout(t);
  }, []);

  // Portal to <body>: the Layout's per-route `.page-enter` wrapper animates a
  // `transform`, and a transformed ancestor would make this `fixed` cover
  // resolve against that (briefly zero-height) wrapper instead of the viewport
  // — collapsing the WebGL canvas to 0px so the wordmark never renders.
  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        background:
          'radial-gradient(120% 95% at 50% 34%, #0f0d12 0%, #08070a 50%, #030304 100%)',
      }}
    >
      {/* a11y: the real heading; the canvas is presentation */}
      <h1 className="sr-only">Gilding</h1>

      {/* the gold — full-bleed, interactive */}
      <GoldSplash className="absolute inset-0" onSettled={onSettled} bindRegild={bindRegild} />

      {/* cinematic dressing above the canvas */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            'radial-gradient(125% 95% at 50% 42%, transparent 40%, rgba(0,0,0,.78) 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-1/2 z-10 opacity-[.045]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          animation: 'gild-grain 1.1s steps(3) infinite',
        }}
      />
      <style>{`
        @keyframes gild-grain{to{transform:translate(6%,4%)}}
        .splash-regild{
          color:#9d8757;
          -webkit-tap-highlight-color:transparent;
          transition:color .25s;
        }
        @media (hover:hover){ .splash-regild:hover{color:#e8c878} }
        .splash-regild:active{color:#e8c878}
      `}</style>

      {/* copy + CTA — rise once the leafing settles */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex flex-col items-center gap-5 px-6 pb-14 text-center transition-all duration-1000"
        style={{
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(14px)',
        }}
      >
        <p
          className="font-display italic"
          style={{ color: '#cdb27a', fontSize: 'clamp(1.05rem,2.4vw,1.5rem)' }}
        >
          Bring a sketch. Leave with gold.
        </p>
        <p
          className="max-w-md font-body"
          style={{ color: '#776745', fontSize: '0.84rem', lineHeight: 1.65 }}
        >
          Turn a rough idea into finished art. Your characters, gilded into scenes
          worth keeping.
        </p>

        <div className="mt-1 flex items-center gap-3" aria-hidden="true">
          <span
            className="h-px w-10"
            style={{ background: 'linear-gradient(90deg,transparent,#c9a546aa)' }}
          />
          <SplashFleuron />
          <span
            className="h-px w-10"
            style={{ background: 'linear-gradient(90deg,#c9a546aa,transparent)' }}
          />
        </div>

        <GildButton className="pointer-events-auto mt-2" onClick={onEnter}>
          Enter site
        </GildButton>

        <button
          type="button"
          onClick={() => regildRef.current?.()}
          className="splash-regild pointer-events-auto mt-1 font-body uppercase"
          style={{ fontSize: '0.62rem', letterSpacing: '0.22em' }}
        >
          Gild again
        </button>
      </div>
    </div>,
    document.body,
  );
}

function SplashFleuron({ className }: { className?: string }) {
  const CLS = className ?? 'h-4 w-7';
  return (
    <svg viewBox="0 0 28 16" aria-hidden="true" className={CLS}>
      <defs>
        <linearGradient id="splashOrnRamp" x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#a87f2c" />
          <stop offset="0.42" stopColor="#e9c873" />
          <stop offset="0.52" stopColor="#fff3ce" />
          <stop offset="0.64" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
      </defs>
      <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#splashOrnRamp)" />
      <path d="M2 8 L26 8" stroke="#fff3ce" strokeWidth="0.5" opacity="0.65" />
      <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
    </svg>
  );
}
